import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { LoadFailed } from '../settings/instantSettings';
import { athensYmd, fmtDMY } from '../home/format';
import {
  ChipTone, Column, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, KeyValue, KeyValueItem, ListPage, ListToolbar,
  StatusChip,
} from '../shared/listPage';

/**
 * Super admin «Ιστορικό ενεργειών» στη διάταξη «Πίνακας» (14/10/2026): ListPage + ListToolbar (κατάστημα,
 * πλήθος εγγραφών) + DataTable + DetailPanel με το «Πριν / Μετά». Ίδια endpoints με πριν:
 * GET /api/admin/audit-logs?limit[&store_id] και GET /api/admin/stores (για το φίλτρο καταστήματος).
 * Οι ενέργειες μένουν όπως τις γράφει ο server (create, update, master_login…): είναι ημερολόγιο ελέγχου,
 * η ακριβής λέξη μετρά περισσότερο από μια μετάφραση.
 */

interface AuditLog {
  id: number;
  store_id: number;
  store_name: string;
  employee_id: number | null;
  employee_name: string | null;
  action: string;
  entity_type: string;
  entity_id: number | null;
  old_values: any;
  new_values: any;
  ip_address: string | null;
  created_at: string;
}

interface StoreOption {
  id: number;
  name: string;
}

type LoadState = 'ready' | 'error' | 'forbidden';

const LIMITS = [50, 100, 250, 500];

// Ο τόνος από λέξη-κλειδί της ενέργειας, με την ίδια σειρά προτεραιότητας όπως πριν (create → … → logout).
const ACTION_TONES: Array<[string, ChipTone]> = [
  ['create', 'ok'],
  ['update', 'brand'],
  ['delete', 'danger'],
  ['login', 'brand'],
  ['logout', 'muted'],
];

export const actionTone = (action: string): ChipTone => {
  const a = (action || '').toLowerCase();
  return ACTION_TONES.find(([key]) => a.includes(key))?.[1] ?? 'muted';
};

// Ώρα Ελλάδας, 24ωρη με δευτερόλεπτα (ο server είναι UTC· ως 14/10 ο browser έγραφε «10:15:58 μ.μ.»).
const TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const when = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : `${fmtDMY(athensYmd(d))} · ${TIME.format(d)}`;
};

const entityOf = (log: AuditLog): string => `${log.entity_type}${log.entity_id ? ` #${log.entity_id}` : ''}`;

const jsonText = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value, null, 2));

const ValuesBlock: React.FC<{ title: string; value: unknown; testId: string }> = ({ title, value, testId }) => (
  <section data-testid={testId}>
    <h3 className="text-sm font-semibold text-t1 mb-2">{title}</h3>
    <pre className="text-xs leading-5 text-t2 bg-surface-2 border border-line rounded-xl p-3 overflow-x-auto max-h-72 whitespace-pre-wrap break-words">
      {jsonText(value)}
    </pre>
  </section>
);

const SuperAdminAuditLogsTab: React.FC = () => {
  const { t } = useTranslation(['superadminOps', 'admin', 'common', 'listPage']);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<LoadState>('ready');
  const [filterStore, setFilterStore] = useState<number | ''>('');
  const [limit, setLimit] = useState(100);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Αλλαγή φίλτρου όσο φορτώνει: μετρά μόνο η απάντηση του τελευταίου αιτήματος.
  const seq = useRef(0);

  // Τα καταστήματα του φίλτρου (μία φορά). Αν αποτύχει, το ημερολόγιο φορτώνει κανονικά, χωρίς φίλτρο.
  useEffect(() => {
    adminAPI.getStores()
      .then((storeData: any) => setStores((storeData || []).map((s: any) => ({ id: s.id, name: s.name }))))
      .catch(err => console.error('Failed to load data:', err));
  }, []);

  const fetchLogs = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const params: any = { limit };
      if (filterStore) params.store_id = filterStore;
      const data = await adminAPI.getAuditLogs(params);
      if (id !== seq.current) return;
      setLogs((data as any) || []);
      setState('ready');
    } catch (err: any) {
      console.error('Failed to load audit logs:', err);
      if (id === seq.current) setState(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [filterStore, limit]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const selected = selectedId !== null ? logs.find(l => l.id === selectedId) ?? null : null;

  const columns = useMemo<Column<AuditLog>[]>(() => [
    {
      key: 'created_at',
      header: t('superadminOps:audit.col.when'),
      width: '188px',
      render: l => <span className="tabular-nums text-t2">{when(l.created_at)}</span>,
    },
    {
      key: 'action',
      header: t('superadminOps:audit.col.action'),
      width: 'minmax(120px, 1fr)',
      stack: 'title',
      render: l => <StatusChip tone={actionTone(l.action)}>{l.action}</StatusChip>,
      // Στο κινητό ο τίτλος της γραμμής: «update · product #12».
      stackRender: l => `${l.action} · ${entityOf(l)}`,
    },
    {
      key: 'entity',
      header: t('superadminOps:audit.col.entity'),
      width: 'minmax(140px, 1.2fr)',
      stack: 'none',
      render: l => <span className="text-t2">{entityOf(l)}</span>,
    },
    {
      key: 'store_name',
      header: t('superadminOps:audit.col.store'),
      width: 'minmax(140px, 1.2fr)',
      render: l => (l.store_name ? <span>{l.store_name}</span> : <span className="text-t4">—</span>),
      stackRender: l => l.store_name || '',
    },
    {
      key: 'employee_name',
      header: t('superadminOps:audit.col.user'),
      width: 'minmax(140px, 1.2fr)',
      render: l => (l.employee_name ? <span>{l.employee_name}</span> : <span className="text-t3">{t('admin:systemUser')}</span>),
      stackRender: l => l.employee_name || t('admin:systemUser'),
    },
    {
      key: 'ip_address',
      header: t('superadminOps:audit.col.ip'),
      width: '136px',
      hideBelow: 'medium',
      render: l => (l.ip_address ? <span className="font-mono text-xs text-t3">{l.ip_address}</span> : <span className="text-t4">—</span>),
    },
  ], [t]);

  const detailItems = (log: AuditLog): KeyValueItem[] => [
    { id: 'when', label: t('superadminOps:audit.col.when'), value: <span className="tabular-nums">{when(log.created_at)}</span> },
    { id: 'action', label: t('superadminOps:audit.col.action'), value: <StatusChip tone={actionTone(log.action)}>{log.action}</StatusChip> },
    { id: 'entity', label: t('superadminOps:audit.col.entity'), value: entityOf(log) },
    { id: 'store', label: t('superadminOps:audit.col.store'), value: log.store_name ? `${log.store_name} (#${log.store_id})` : null },
    { id: 'user', label: t('superadminOps:audit.col.user'), value: log.employee_name || t('admin:systemUser') },
    { id: 'ip', label: t('superadminOps:audit.col.ip'), value: log.ip_address ? <span className="font-mono">{log.ip_address}</span> : null },
  ];

  const firstLoad = loading && logs.length === 0;
  const summary = state === 'ready' && !firstLoad
    ? (logs.length >= limit
      ? t('superadminOps:audit.countLatest', { count: logs.length })
      : t('superadminOps:audit.count', { count: logs.length }))
    : null;

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.system')}
      title={t('superadminOps:audit.title')}
      subtitle={t('superadminOps:audit.subtitle')}
      testId="sa-audit"
    >
      <ListToolbar
        filters={(
          <>
            <FilterSelect
              label={t('superadminOps:audit.storeFilter')}
              value={filterStore === '' ? '' : String(filterStore)}
              onChange={v => setFilterStore(v === '' ? '' : Number(v))}
              allLabel={t('admin:allStores')}
              options={stores.map(s => ({ value: String(s.id), label: `${s.name} (#${s.id})` }))}
              testId="sa-audit-store"
            />
            {/* Πλήθος εγγραφών: πάντα κάποια τιμή, άρα όχι FilterSelect (θα έδειχνε μόνιμα «φιλτράρει»). */}
            <select
              className="lp-select"
              aria-label={t('superadminOps:audit.limitLabel')}
              title={t('superadminOps:audit.limitLabel')}
              value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              data-testid="sa-audit-limit"
            >
              {LIMITS.map(n => <option key={n} value={n}>{t('superadminOps:audit.limitOption', { count: n })}</option>)}
            </select>
          </>
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => fetchLogs()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('superadminOps:refresh')}
          </button>
        )}
        summary={summary}
      />

      {state !== 'ready' ? (
        <LoadFailed
          message={state === 'forbidden' ? t('superadminOps:forbidden') : t('superadminOps:audit.loadFailed')}
          onRetry={() => fetchLogs()}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={logs}
          rowKey={l => l.id}
          onRowClick={l => setSelectedId(l.id)}
          selectedKey={selectedId}
          loading={loading}
          dense
          stickyHeader
          rowLabel={l => t('superadminOps:audit.rowLabel', { action: l.action, entity: entityOf(l), when: when(l.created_at) })}
          empty={<EmptyState title={t('admin:noLogsFound')} hint={filterStore ? t('superadminOps:audit.emptyFiltered') : undefined} />}
          testId="sa-audit-table"
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected ? selected.action : ''}
        subtitle={selected ? `${entityOf(selected)} · ${when(selected.created_at)}` : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelectedId(null)}>{t('listPage:close')}</button>}
        testId="sa-audit-detail"
      >
        {selected && (
          <>
            <KeyValue items={detailItems(selected)} testId="sa-audit-values" />
            {selected.old_values || selected.new_values ? (
              <div className="mt-6 space-y-5">
                {selected.old_values && <ValuesBlock title={t('admin:beforeValues')} value={selected.old_values} testId="sa-audit-before" />}
                {selected.new_values && <ValuesBlock title={t('admin:afterValues')} value={selected.new_values} testId="sa-audit-after" />}
              </div>
            ) : (
              <p className="mt-6 text-sm text-t3">{t('superadminOps:audit.noValues')}</p>
            )}
          </>
        )}
      </DetailPanel>
    </ListPage>
  );
};

export default SuperAdminAuditLogsTab;
